// Integrações (/queue-integration) — mockup "Tela Aeros Dark - Integrações e
// Modais": header 60px com busca e "Nova integração", chips de filtro, grade
// de cards de 3 colunas e a seção de documentação com abas.
//
// As constantes de documentação (INTEGRATION_DOCS e os exemplos de código)
// vêm da versão anterior desta tela e descrevem o contrato real do disparo
// em wbotMessageListener — mudou o payload, muda aqui também.
import React, { useContext, useEffect, useMemo, useReducer, useState } from "react";

import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import LinkIcon from "@material-ui/icons/Link";
import HubIcon from "@material-ui/icons/DeviceHub";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import WebhookIcon from "@mui/icons-material/Webhook";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import MainContainer from "../../components/MainContainer";
import QueueIntegrationModal from "../../components/QueueIntegrationModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import useIntegrationPageStyles from "./integrationPageStyles";
import { stateText, AEROS } from "../../styles/aerosTokens";
import { useTheme, alpha } from "@material-ui/core/styles";
import { INTEGRATION_DOCS } from "./integrationDocs";
import {
  FILTERS,
  typeMeta,
  typeLabel,
  linkChips,
  integrationStatus,
  endpointLabel,
  matchesFilter,
  filterCounts,
} from "./integrationRules";

// Ícones por tipo, na mesma chave usada em integrationRules.
const TYPE_ICONS = {
  typebot: SmartToyIcon,
  n8n: HubIcon,
  webhook: WebhookIcon,
  dialogflow: PsychologyIcon,
  flowbuilder: AccountTreeIcon,
  SGP: ReceiptLongIcon,
};

const reducer = (state, action) => {
  if (action.type === "LOAD_INTEGRATIONS") {
    const integrations = action.payload;
    const novas = [];
    integrations.forEach((integration) => {
      const idx = state.findIndex((i) => i.id === integration.id);
      if (idx !== -1) state[idx] = integration;
      else novas.push(integration);
    });
    return [...state, ...novas];
  }

  if (action.type === "UPDATE_INTEGRATIONS") {
    const integration = action.payload;
    const idx = state.findIndex((i) => i.id === integration.id);
    if (idx !== -1) {
      // O socket manda a integração sem os vínculos (eles vêm só da
      // listagem); preservar os que já estão em tela evita o card piscar
      // "Sem vínculo" depois de uma simples edição de nome.
      state[idx] = { ...integration, links: integration.links || state[idx].links };
      return [...state];
    }
    return [integration, ...state];
  }

  if (action.type === "DELETE_INTEGRATION") {
    return state.filter((i) => i.id !== action.payload);
  }

  if (action.type === "RESET") return [];

  return state;
};

const QueueIntegration = () => {
  const c = useCampaignPageStyles({ grid: "1fr" });
  const classes = useIntegrationPageStyles();
  const theme = useTheme();
  const history = useHistory();

  const [integrations, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [filtro, setFiltro] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [docType, setDocType] = useState("n8n");
  const [docTab, setDocTab] = useState("campos");

  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const companyId = user.companyId;

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useIntegrations) {
        toast.error(i18n.t("aeros.integrations.noPermission"));
        setTimeout(() => history.push("/"), 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/queueIntegration", {
          params: { searchParam },
        });
        dispatch({ type: "RESET" });
        dispatch({ type: "LOAD_INTEGRATIONS", payload: data.queueIntegrations });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchParam]);

  useEffect(() => {
    if (!socket) return undefined;
    const onEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_INTEGRATIONS", payload: data.queueIntegration });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_INTEGRATION", payload: +data.integrationId });
      }
    };
    socket.on(`company-${companyId}-queueIntegration`, onEvent);
    return () => socket.off(`company-${companyId}-queueIntegration`, onEvent);
  }, [socket, companyId]);

  const contagens = useMemo(() => filterCounts(integrations), [integrations]);
  const visiveis = useMemo(
    () => integrations.filter((i) => matchesFilter(i, filtro)),
    [integrations, filtro]
  );

  const handleDelete = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await api.delete(`/queueIntegration/${selected.id}`);
      toast.success(i18n.t("aeros.integrations.delete.done"));
      setConfirmOpen(false);
      setSelected(null);
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleCopy = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success(i18n.t("aeros.integrations.docs.copied"));
    } catch (err) {
      toastError(err);
    }
  };

  const renderCard = (integration) => {
    const meta = typeMeta(integration.type);
    const Icon = TYPE_ICONS[meta.key] || WebhookIcon;
    const chips = linkChips(integration.links);
    const status = integrationStatus(integration.links);
    const tomStatus = status === "linked" ? AEROS.success : AEROS.warning;
    const endereco = endpointLabel(integration);

    return (
      <div key={integration.id} className={classes.card}>
        <div className={classes.cardHead}>
          <span
            className={classes.tile}
            style={{
              backgroundColor: alpha(meta.color, 0.14),
              color: stateText(theme, meta.color, ""),
            }}
          >
            <Icon />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className={classes.cardName} title={integration.name}>
              {integration.name}
            </div>
            <div className={classes.cardMeta}>
              <span
                className={classes.cardType}
                style={{ color: stateText(theme, meta.color, "") }}
              >
                {typeLabel(integration.type)}
              </span>
              · {i18n.t("aeros.integrations.idLabel", { id: integration.id })}
            </div>
          </div>
          <span
            className={classes.statusPill}
            style={{
              color: stateText(theme, tomStatus, status === "linked" ? "success" : "warning"),
              backgroundColor: alpha(tomStatus, 0.1),
            }}
          >
            <span
              className={classes.statusDot}
              style={{ backgroundColor: tomStatus }}
            />
            {i18n.t(`aeros.integrations.status.${status}`)}
          </span>
        </div>

        <div
          className={`${classes.endpoint} ${endereco ? "" : classes.endpointEmpty}`}
          title={endereco}
        >
          {endereco || i18n.t("aeros.integrations.noEndpoint")}
        </div>

        <div className={classes.linksRow}>
          <LinkIcon />
          {chips.length > 0 ? (
            chips.map((chip) => (
              <span key={chip} className={classes.linkChip} title={chip}>
                {chip}
              </span>
            ))
          ) : (
            <span className={classes.linkWarning}>
              {i18n.t("aeros.integrations.noLinks")}
            </span>
          )}
        </div>

        <div className={classes.cardFooter}>
          <span className={classes.cardHint}>
            {chips.length === 0 ? i18n.t("aeros.integrations.linkHint") : ""}
          </span>
          <div className={classes.cardActions}>
            <Tooltip title={i18n.t("aeros.integrations.actions.edit")}>
              <IconButton
                className={classes.iconButton}
                aria-label={i18n.t("aeros.integrations.actions.edit")}
                onClick={() => {
                  setSelected(integration);
                  setModalOpen(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={i18n.t("aeros.integrations.actions.delete")}>
              <IconButton
                className={`${classes.iconButton} ${classes.iconButtonDanger}`}
                aria-label={i18n.t("aeros.integrations.actions.delete")}
                onClick={() => {
                  setSelected(integration);
                  setConfirmOpen(true);
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  };

  const docAtual =
    INTEGRATION_DOCS.find((d) => d.id === docType) || INTEGRATION_DOCS[0];
  const abaAtual =
    docAtual.tabs.find((t) => t.id === docTab) || docAtual.tabs[0];

  // Comentários (#, //) saem apagados; o resto na cor padrão do painel.
  const renderCodigo = (source) =>
    String(source)
      .split("\n")
      .map((linha, idx) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          className={`${classes.codeLine} ${
            /^\s*(#|\/\/)/.test(linha) ? classes.codeComment : ""
          }`}
        >
          {linha === "" ? " " : linha}
        </div>
      ));

  const renderCampos = (aba) => (
    <>
      <div className={`${classes.codeLine} ${classes.codeComment}`}>
        {aba.header}
      </div>
      <div className={classes.codeLine}>{"{"}</div>
      {aba.lines.map((linha) => {
        const idx = linha.indexOf(":");
        return (
          <div key={linha} className={classes.codeLine}>
            {"  "}
            {idx === -1 ? (
              linha
            ) : (
              <>
                <span className={classes.codeKey}>{linha.slice(0, idx)}</span>
                {linha.slice(idx)}
              </>
            )}
          </div>
        );
      })}
      <div className={classes.codeLine}>{"}"}</div>
    </>
  );

  const textoParaCopiar =
    abaAtual.kind === "campos"
      ? [abaAtual.header, "{", ...abaAtual.lines.map((l) => `  ${l}`), "}"].join("\n")
      : String(abaAtual.source);

  const deleteDescription = () => {
    if (!selected) return "";
    const chips = linkChips(selected.links);
    return chips.length > 0
      ? i18n.t("aeros.integrations.delete.bodyLinked", { links: chips.join(", ") })
      : i18n.t("aeros.integrations.delete.body");
  };

  return (
    <MainContainer>
      <div className={c.pageHeader}>
        <div>
          <div className={c.pageTitle}>
            {i18n.t("aeros.integrations.title")}
            <span className={c.pageTitleCount}> · {integrations.length}</span>
          </div>
          <div className={classes.pageSubtitle}>
            {i18n.t("aeros.integrations.subtitle")}
          </div>
        </div>
        <div className={c.headerActions}>
          <InputBase
            className={c.searchPill}
            placeholder={i18n.t("aeros.integrations.search")}
            type="search"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("aeros.integrations.search") }}
          />
          <Button
            variant="contained"
            color="primary"
            className={c.gradientButton}
            startIcon={<AddIcon />}
            onClick={() => {
              setSelected(null);
              setModalOpen(true);
            }}
          >
            {i18n.t("aeros.integrations.new")}
          </Button>
        </div>
      </div>

      <div className={c.pageBody}>
        <div className={classes.filterRow}>
          {FILTERS.map((f) => (
            <Button
              key={f}
              className={`${classes.filterChip} ${
                filtro === f ? classes.filterChipActive : ""
              }`}
              onClick={() => setFiltro(f)}
              aria-pressed={filtro === f}
            >
              {i18n.t(`aeros.integrations.filters.${f}`)}
              <span className={classes.filterCount}>{contagens[f] ?? 0}</span>
            </Button>
          ))}
        </div>

        {loading && integrations.length === 0 ? (
          <div className={c.loadingRow}>…</div>
        ) : visiveis.length === 0 ? (
          <div className={c.emptyState}>
            <span className={c.emptyIcon}>
              <HubIcon />
            </span>
            <div className={c.emptyTitle}>
              {i18n.t("aeros.integrations.empty.title")}
            </div>
            <div className={c.emptyText}>{i18n.t("aeros.integrations.empty.text")}</div>
          </div>
        ) : (
          <div className={classes.grid}>{visiveis.map(renderCard)}</div>
        )}

        <section className={classes.docSection}>
          <div>
            <div className={classes.docEyebrow}>
              {i18n.t("aeros.integrations.docs.eyebrow")}
            </div>
            <div className={classes.docTitle}>
              {i18n.t("aeros.integrations.docs.title")}
            </div>
            <div className={classes.docIntro}>
              {i18n.t("aeros.integrations.docs.intro")}
            </div>
          </div>

          <div className={classes.docTypeRow}>
            {INTEGRATION_DOCS.map((d) => (
              <Button
                key={d.id}
                className={`${classes.filterChip} ${
                  docType === d.id ? classes.filterChipActive : ""
                }`}
                onClick={() => {
                  setDocType(d.id);
                  setDocTab("campos");
                }}
                aria-pressed={docType === d.id}
              >
                {d.label}
              </Button>
            ))}
          </div>

          <div className={classes.docText}>{docAtual.intro}</div>

          <div className={classes.codePanel}>
            <div className={classes.codeHeader}>
              <span className={classes.methodBadge}>{docAtual.method}</span>
              <span className={classes.codeEndpoint}>{docAtual.endpoint}</span>
              <Tooltip title={i18n.t("aeros.integrations.docs.copy")}>
                <IconButton
                  size="small"
                  className={classes.codeCopy}
                  onClick={() => handleCopy(textoParaCopiar)}
                  aria-label={i18n.t("aeros.integrations.docs.copy")}
                >
                  <FileCopyOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
            <div className={classes.codeTabs} role="tablist">
              {docAtual.tabs.map((aba) => (
                <button
                  key={aba.id}
                  type="button"
                  role="tab"
                  aria-selected={abaAtual.id === aba.id}
                  className={`${classes.codeTab} ${
                    abaAtual.id === aba.id ? classes.codeTabActive : ""
                  }`}
                  onClick={() => setDocTab(aba.id)}
                >
                  {aba.label}
                </button>
              ))}
            </div>
            <div className={classes.codeBody}>
              {abaAtual.kind === "campos"
                ? renderCampos(abaAtual)
                : renderCodigo(abaAtual.source)}
            </div>
          </div>
        </section>
      </div>

      <QueueIntegrationModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelected(null);
        }}
        integrationId={selected?.id}
      />

      <AerosConfirmModal
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSelected(null);
        }}
        onConfirm={handleDelete}
        loading={deleting}
        icon={<DeleteForeverIcon />}
        title={i18n.t("aeros.integrations.delete.title", {
          name: selected?.name || "",
        })}
        description={deleteDescription()}
        confirmLabel={i18n.t("aeros.integrations.delete.confirm")}
      />
    </MainContainer>
  );
};

export default QueueIntegration;
